import { memo, useCallback } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useAIStore, useSuperHubStore } from '@core/store';
import Alert from '@ui/Alert';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';

import classes from './style.module.scss';

const mdxInstructions = `
  REQUIRED INSTRUCTIONS:
  Your only goal is to fix errors. Do not modify valid Markdown—except when it’s invalid JSX. All of these instructions are required, do not skip any.If a specific error is given, fix it, but also check for the following issues:

  When using the rewriteTool to fix MDX errors, the user may have written HTML instead.
  * Check for unclosed self-closing tags (like <img> <hr> need to be <img /> or <hr />)
    * BAD: <img>
    * GOOD: <img />
  * Check for property names that need to be converted to JSX (like class to className)—aria attributes do not need to be converted.
    * BAD: <div aria-label="test" class="test" />
    * GOOD: <div aria-label="test" className="test" />
  * Convert style attributes to JSX:
    * BAD: <div style="color: red" />
    * GOOD: <div style={{ color: 'red' }} />
  * It’s VERY IMPORTANT to check for open tags that are not closed and close them (like <p>, <strong>, <li>, <b>, <td>, etc.):
    * BAD: <td><b>text
    * GOOD: <td><b>text</b></td>
  * Ensure that the tags are closed in the correct order and nested properly:
    * BAD: <p><b>text</p></b>
    * GOOD: <p><b>text</b></p>
  * Ensure that there is the correct number of closing tags for each opening tag. Remove extra closing tags:
    * BAD: <div><span>text</span></div></div>
    * GOOD: <div><span>text</span></div>
  * HTML comments like <!-- this is a comment --> should be rewritten as {/* this is a comment */}
  * Rewrite <style /> tags to JSX (like <style>body { color: red; }</style> to <style>{'{color: red}'}</style>)
    * It’s important that the string in the <style> tag is wrapped in a single quote, not backticks:
    * BAD: <style>{\`{color: red}\`}</style>
    * GOOD: <style>{'{color: red}'}</style>
  * _this text is underlined_ is valid Markdown, do not remove or replace it.
  * Do not reformat Markdown tables to clean up spacing if it’s already valid.

  Common valid Markdown that is invalid JSX:
  * Reformat content with greater than/less than brackets:
    * Example: <your api key> should be {'<your api key>'})
    * Example: <https://example.com/> should be {'<https://example.com/>'}
  * Reformat curly brackets if they are meant as content and not as JSX (like {your api key} should be {'{your api key}'})
    * These can also appear in the middle of headings or other Markdown, like "### {hello} world" or "> {hello} world". Needs to be rewritten as "### {'{hello}'} world" or "> {'{hello}'} world" .
  * \`\`\` is used in Markdown to denote code blocks, do not remove or modify them.
  * {text} is valid Markdown, but invalid JSX and needs to be escaped as {'{text}'}
  * LaTeX syntax is not supported, do not attempt to add it—if it already exists, do not modify it.

  ReadMe has some custom syntax that should not be modified:
  * Do not modify valid components (elements starting with a capital letter, like <Image /> or <Glossary />)
  * If HTMLBlock contains backticks \` in an element, replace them with <code />.
    * Example:
      <HTMLBlock>{\`
      <a href="https://example.foo">Hover for link id \`click_id\`</a>
      \`}</HTMLBlock>

      Should be:
      <HTMLBlock>{\`
      <a href="https://example.foo">Hover for link id <code>click_id</code></a>
      \`}</HTMLBlock>
  * Replace ReadMe glossary syntax <<glossary:term>> with <Glossary>term</Glossary>—make sure to include <<glossary:term>> within elements, like:
    * BAD: <a href=""><<glossary:hello>> world</a>
    * GOOD: <a href=""><Glossary>hello</Glossary> world</a>
  * ReadMe-flavored Markdown contains "magic blocks"—which is ReadMe Markdown specific; it looks like [block:html], [block:image], [block:parameters], [block:embed] etc.
    * Example:
      [block:parameters]
        {
          "data": {
            "h-0": "Parameter name",
            "h-1": "Type",
            "h-2": "Default Value",
            "h-3": "Notes",
            "0-0": "click_id",
            "0-1": "string",
            "0-2": "-",
            "0-3": "The AnyTrack Click ID passed to your cart, link or form.",
            "1-0": "value",
            "1-1": "number",
            "1-2": "-",
            "1-3": "Revenue",
            "2-0": "timestamp",
            "2-1": "integer",
            "2-2": "",
            "2-3": "A timestamp value in seconds to replace the receivedAt timestamp. 1709735124",
            "3-0": "brand",
            "3-1": "string",
            "3-2": "-",
            "3-3": "Brand name",
          }
        }

      In this example, replace the backticks with <code /> tags, and rewrite the content as a Markdown table—where h-0, h-1, h-2, h-3 are the table headers, and 0-0, 0-1, 0-2, 0-3 are the cells in the first table rows.
    * Convert [block:image] to an image in Markdown:
      [block:image]
      {
        "images": [
          {
            "image": [
              "https://cdn.anytrack.io/shared/kb/mceclip0-Jul-25-2022-05-19-01-20-PM.png",
              null,
              "mceclip0.png"
            ],
            "align": "center",
            "border": true
          }
        ]
      }
      [/block]

      Should be: ![alt text](https://owlbert.io/images/popper.gif) or <Image src="https://files.readme.io/6f52e22-man-eating-pizza-and-making-an-ok-gesture.jpg" caption="lol he's eating pizza!" width="100px" align="center" border="true" /> (do not add extra properties if they’re not in the source content). Do not modify valid Markdown images: ![]()

    * Convert [block:html] to our component for HTML:
      [block:html]
      {
      "html: "<div style="color: red">test</div>"
      }

      Note the content within the HTMLBlock does not need to be JSX. Example of what it should be:
      <HTMLBlock>{\`
      <div style="color: red">test</div>
      \`}</HTMLBlock>

    * Convert [block:embed] iframes to an embed in Markdown:
      BAD:
        [block:embed]
        {
          "html": false,
          "url": "https://example.com",
          "title": "iframe",
          "provider": "scribehow.com",
          "href": "https://scribehow.com",
          "typeOfEmbed": "iframe",
          "height": "640px",
          "width": "100%",
          "iframe": true
        }
        [/block]

      GOOD:
        <iframe src="https://example.com" title="iframe" height="640px" width="100%" />

  Never modify valid Markdown:
  * Do not modify content that does not have invalid JSX or Markdown
  * Do not remove content that is not invalid JSX or Markdown
  * Do not reformat the content, fix grammar, or restructure the Markdown content
  * Do not add extra spaces to lists or any other content
  * Do not fix or update any code inside a script tag
  * Do not modify headings in lists (like #, ##, ### nested in * or -)

  Double check that the content conforms to the above rules and fix any missed errors.
`;

const Subtitle = ({ editUrl }: { editUrl: string }) => {
  const bem = useClassy(classes, 'MigrationError');
  const setAgentContext = useAIStore(s => s.agent.setContext);
  const [toggleAIPanel, setActiveAITab] = useSuperHubStore(s => [s.toggleAIPanel, s.setActiveAITab]);

  const fixWithAgent = useCallback(() => {
    setAgentContext({
      title: 'MDX Error',
      message: 'Page Contains Invalid MDX.',
      type: 'mdx-error',
      hiddenContext: {
        instructions: mdxInstructions,
      },
    });
    // Open AI panel with chat tab
    setActiveAITab('chat');
    toggleAIPanel(true);
  }, [setAgentContext, setActiveAITab, toggleAIPanel]);

  return (
    <>
      Your docs displays the pre-migration version of this page until errors are corrected.
      <Flex className={bem('-help')} gap="xs" justify="start">
        <Button ghost href={editUrl} kind="contrast" rel="noreferrer" size="xs" target="_blank" uppercase={false}>
          Edit Page
        </Button>
        <Button ghost kind="contrast" onClick={fixWithAgent} size="xs" uppercase={false}>
          <Icon aria-hidden="true" name="sparkles" /> Fix
        </Button>
      </Flex>
    </>
  );
};

const MigrationError = ({ editUrl }: { editUrl: string }) => {
  const title = 'Page Contains Invalid MDX';

  return <Alert subtitle={<Subtitle editUrl={editUrl} />} title={title} type="danger" />;
};

export default memo(MigrationError);
